/**
 * DashRequests — Phase 6
 * Business-side management for customer requests (purchase / finance /
 * insurance / rental / …).
 */
import React, { useCallback, useEffect, useState } from "react";
import { ClipboardList, RefreshCw, MessageCircle, ChevronRight, Check, X, User, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

import { useI18n } from "../../../lib/i18n";
import { commsAPI, REQUEST_KINDS, REQUEST_STATUS_LABELS } from "../../../lib/comms";

const STATUS_TABS = [
  { key: "",            ar: "الكل",       en: "All"        },
  { key: "open",        ar: "مفتوح",       en: "Open"       },
  { key: "in_progress", ar: "قيد الدراسة", en: "In progress" },
  { key: "won",         ar: "مقبول",       en: "Won"        },
  { key: "lost",        ar: "مرفوض",       en: "Lost"       },
  { key: "closed",      ar: "مغلق",        en: "Closed"     },
];
const TONE_CLASSES = {
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blue:  "bg-blue-50 text-blue-700 border-blue-200",
  red:   "bg-red-50 text-red-700 border-red-200",
  gray:  "bg-neutral-100 text-neutral-700 border-neutral-200",
};
function kindLabel(k, lang) {
  const f = REQUEST_KINDS.find((x) => x.key === k);
  return f ? (lang === "ar" ? f.ar : f.en) : k;
}

export default function DashRequests({ businessId }) {
  const { lang } = useI18n();
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [noteText, setNoteText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await commsAPI.businessRequests(businessId, status ? { status, limit: 100 } : { limit: 100 });
      setData(d);
    } catch {
      toast.error(lang === "ar" ? "تعذّر تحميل الطلبات" : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [businessId, status, lang]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (r, newStatus, note = "") => {
    try {
      const updated = await commsAPI.updateRequestStatus(businessId, r.id, { status: newStatus, note });
      toast.success(lang === "ar" ? "تم التحديث" : "Updated");
      setActive(updated);
      await load();
    } catch { toast.error(lang === "ar" ? "فشل التحديث" : "Failed"); }
  };

  const addNote = async () => {
    if (!noteText.trim() || !active) return;
    try {
      const updated = await commsAPI.addInternalNote(businessId, active.id, noteText.trim());
      setActive(updated);
      setNoteText("");
      toast.success(lang === "ar" ? "تمت الإضافة" : "Note added");
    } catch { toast.error(lang === "ar" ? "فشل" : "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="dash-requests">
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            type="button"
            key={t.key || "all"}
            onClick={() => setStatus(t.key)}
            className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
              status === t.key
                ? "bg-[#0a0a0a] text-[#f7eecd]"
                : "bg-white border border-[#0a0a0a]/10 text-[#525252] hover:text-[#0a0a0a]"
            }`}
            data-testid={`requests-tab-${t.key || "all"}`}
          >
            {lang === "ar" ? t.ar : t.en}
          </button>
        ))}
        <button type="button" onClick={load}
                className="ms-auto inline-flex items-center gap-1.5 text-[12px] text-[#525252] hover:text-[#0a0a0a]"
                data-testid="requests-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
          {lang === "ar" ? "تحديث" : "Refresh"}
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#8a8a8a]">…</div>
      ) : data.items.length === 0 ? (
        <div className="p-10 text-center rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10 text-[#8a8a8a]"
             data-testid="requests-empty">
          <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {lang === "ar" ? "لا توجد طلبات" : "No requests"}
        </div>
      ) : (
        <div className="grid gap-2">
          {data.items.map((r) => {
            const label = REQUEST_STATUS_LABELS[r.status] || { ar: r.status, en: r.status, tone: "gray" };
            return (
              <button
                type="button"
                key={r.id}
                onClick={() => setActive(r)}
                className="w-full text-start rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-4 hover:border-[#c9a55c]/50 transition-colors flex items-center gap-3"
                data-testid={`request-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className="text-[13px] font-bold text-[#0a0a0a]">{kindLabel(r.kind, lang)}</div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${TONE_CLASSES[label.tone]}`}>
                      {lang === "ar" ? label.ar : label.en}
                    </span>
                  </div>
                  <div className="text-[13px] text-[#525252] truncate">
                    {r.subject || r.body || (lang === "ar" ? "بدون عنوان" : "Untitled")}
                  </div>
                  {(r.customer_name || r.customer_phone) && (
                    <div className="text-[11px] text-[#525252] mt-1 flex items-center gap-2 flex-wrap" data-testid={`request-${r.id}-contact`}>
                      {r.customer_name && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{r.customer_name}</span>}
                      {r.customer_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.customer_phone}</span>}
                    </div>
                  )}
                  <div className="text-[11px] text-[#8a8a8a] mt-0.5">
                    {new Date(r.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#8a8a8a]" />
              </button>
            );
          })}
        </div>
      )}

      {/* Detail drawer/modal */}
      {active && (
        <>
          <button type="button" aria-label="close" onClick={() => setActive(null)}
                  className="fixed inset-0 z-40 bg-black/40" />
          <div className="fixed inset-x-3 top-6 bottom-6 sm:inset-x-auto sm:right-6 sm:top-16 sm:bottom-8 sm:w-[520px] z-50 rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col"
               data-testid="request-drawer">
            <div className="p-5 border-b border-[#0a0a0a]/[0.06]">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <div className="text-lg font-bold text-[#0a0a0a]">{kindLabel(active.kind, lang)}</div>
                {(() => {
                  const label = REQUEST_STATUS_LABELS[active.status] || {};
                  return (
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${TONE_CLASSES[label.tone]}`}>
                      {lang === "ar" ? label.ar : label.en}
                    </span>
                  );
                })()}
                <button type="button" onClick={() => setActive(null)}
                        className="ms-auto text-[#8a8a8a] hover:text-[#0a0a0a]">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-[13px] font-semibold text-[#0a0a0a]">{active.subject}</div>
              <div className="text-[12px] text-[#525252] mt-1 whitespace-pre-wrap">{active.body}</div>
              {/* Customer contact block — surfaces the data the customer
                  submitted (name/phone/email) so the business can act on
                  the lead. Previously stored but never rendered. */}
              {(active.customer_name || active.customer_phone || active.customer_email) && (
                <div className="mt-3 rounded-xl p-3" style={{ background: "#fdf9ef", border: "1px solid #c9a55c33" }} data-testid="request-customer-contact">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-2">
                    {lang === "ar" ? "بيانات العميل" : "Customer contact"}
                  </div>
                  <div className="grid gap-1.5 text-[12px]">
                    {active.customer_name && (
                      <div className="flex items-center gap-2 text-[#0a0a0a]" data-testid="customer-name">
                        <User className="w-3.5 h-3.5 text-[#8a6f2e]" />
                        <span className="font-semibold">{active.customer_name}</span>
                      </div>
                    )}
                    {active.customer_phone && (
                      <a href={`tel:${active.customer_phone}`} className="flex items-center gap-2 text-[#0a0a0a] hover:text-[#8a6f2e]" data-testid="customer-phone">
                        <Phone className="w-3.5 h-3.5 text-[#8a6f2e]" />
                        <span className="font-mono" dir="ltr">{active.customer_phone}</span>
                      </a>
                    )}
                    {active.customer_email && (
                      <a href={`mailto:${active.customer_email}`} className="flex items-center gap-2 text-[#0a0a0a] hover:text-[#8a6f2e]" data-testid="customer-email">
                        <Mail className="w-3.5 h-3.5 text-[#8a6f2e]" />
                        <span className="font-mono" dir="ltr">{active.customer_email}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}
              {active.payload && Object.keys(active.payload).length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {Object.entries(active.payload).map(([k, v]) => (
                    <div key={k} className="text-[11px] bg-[#faf9f5] rounded-lg px-2 py-1">
                      <span className="text-[#8a6f2e] uppercase font-bold">{k}:</span> {String(v)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-5 flex-1 overflow-y-auto">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-2">
                {lang === "ar" ? "تحديث الحالة" : "Update status"}
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                <button type="button" onClick={() => updateStatus(active, "in_progress")}
                        className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-blue-600 text-white"
                        data-testid="req-status-progress">
                  {lang === "ar" ? "قيد الدراسة" : "In Progress"}
                </button>
                <button type="button" onClick={() => updateStatus(active, "won")}
                        className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-emerald-600 text-white inline-flex items-center gap-1"
                        data-testid="req-status-won">
                  <Check className="w-3.5 h-3.5" /> {lang === "ar" ? "موافقة" : "Won"}
                </button>
                <button type="button" onClick={() => updateStatus(active, "lost")}
                        className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-red-50 text-red-700 border border-red-200"
                        data-testid="req-status-lost">
                  {lang === "ar" ? "رفض" : "Lost"}
                </button>
                <button type="button" onClick={() => updateStatus(active, "closed")}
                        className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-neutral-200 text-neutral-800">
                  {lang === "ar" ? "إغلاق" : "Close"}
                </button>
              </div>

              <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-2 mt-4">
                {lang === "ar" ? "ملاحظات داخلية" : "Internal notes"}
              </div>
              <div className="space-y-2 mb-3">
                {(active.internal_notes || []).length === 0 ? (
                  <div className="text-[12px] text-[#8a8a8a]">
                    {lang === "ar" ? "لا توجد ملاحظات" : "No notes yet"}
                  </div>
                ) : (
                  active.internal_notes.map((n, idx) => (
                    <div key={idx} className="text-[12px] bg-[#faf9f5] rounded-lg px-3 py-2">
                      <div className="text-[#0a0a0a]">{n.text}</div>
                      <div className="text-[10px] text-[#8a8a8a] mt-1">
                        {new Date(n.at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <input type="text" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                       placeholder={lang === "ar" ? "أضف ملاحظة…" : "Add a note…"}
                       className="flex-1 px-3 py-2 rounded-full border border-[#0a0a0a]/15 text-sm"
                       data-testid="req-note-input" />
                <button type="button" onClick={addNote}
                        className="px-3 py-2 rounded-full text-[12px] font-bold bg-[#0a0a0a] text-[#f7eecd] inline-flex items-center gap-1"
                        data-testid="req-note-submit">
                  <MessageCircle className="w-3.5 h-3.5" />
                  {lang === "ar" ? "إضافة" : "Add"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
